'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Menu,
  HttpEditor,
  LabelDefault,
  MetaDefault,
  type BuiltInActionId,
  type HttpEditorProps,
} from '@terra/ui-design-system';
import { Copy01Icon, PlayCircleIcon } from '@terra/ui-icons';
import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import {
  useCachedCredentials,
  useRefreshCredential,
} from '../../hooks/useCachedCredentials';
import { AuthCredentialMenuContent } from './AuthCredentialMenu';
import {
  CredentialToolbarDropdown,
  applyCredentialToRequestText,
  type ActiveCredential,
  type CredentialWithRequest,
} from './CredentialToolbarDropdown';
import { getHttp1TerminatorError } from '../../utils/httpRequestValidation';
import type { CachedCredentialDto } from '@terra/shared-contracts-authenticator';
import {
  parseHeaders,
  replaceHeaderValue,
  buildCookieHeader,
  isAuthHeader,
  preserveAuthScheme,
  type ParsedHeader,
} from '../../utils/authHeaderDetection';
import { hasReplayRunAllContext } from './credentialDataUtils';
import { useApplicationScriptConfig } from '../../store/preRequestScriptStore';

type ReplayRequestEditorProps = {
  value: string;
  onChange: (value: string) => void;
  applicationId?: string;
  height?: string | number;
  placeholder?: string;
  readOnly?: boolean;
  activeCredential: ActiveCredential;
  onCredentialApplied?: (credential: ActiveCredential) => void;
  /** Optional callback for "Send to Chat" action (content, selectedText?) */
  onSendToChat?: (content: string, selectedText?: string) => void;
  onSendToReplay?: (content: string) => void;
  /** Optional callback to run the request with all cached credentials at once */
  onRunAllCredentials?: (
    credentialsWithRequests: CredentialWithRequest[],
  ) => void;
} & Pick<
  HttpEditorProps,
  | 'showGrpcBodyViewToggle'
  | 'grpcBodyView'
  | 'onGrpcBodyViewChange'
  | 'grpcBodyViewToggleDisabled'
  | 'canonicalRequestUrl'
>;
/**
 * Enhanced HttpEditor with context menu for auth header replacement
 * Features built-in search/replace functionality (Cmd+F)
 */
const HIDDEN_BUILT_IN_ACTIONS: BuiltInActionId[] = ['encode-url', 'decode-url'];

export const ReplayRequestEditor = React.memo(function ReplayRequestEditor({
  value,
  onChange,
  applicationId,
  height = '100%',
  placeholder,
  readOnly = false,
  activeCredential,
  onCredentialApplied,
  onSendToChat,
  onSendToReplay,
  canonicalRequestUrl,
  onRunAllCredentials,
  showGrpcBodyViewToggle,
  grpcBodyView,
  onGrpcBodyViewChange,
  grpcBodyViewToggleDisabled,
}: ReplayRequestEditorProps) {
  const t = useTranslations('replay.contextMenu');
  const { credentials, isLoading: isLoadingCredentials } = useCachedCredentials(
    {
      applicationId,
      enabled: !!applicationId,
    },
  );
  const refreshCredentialMutation = useRefreshCredential();
  const { activePreset, script: appScript } =
    useApplicationScriptConfig(applicationId);
  const hasActivePreRequestScript = !!activePreset && !!appScript.trim();
  const [refreshingIds, setRefreshingIds] = useState<Set<string>>(new Set());
  const [refreshedIds, setRefreshedIds] = useState<Set<string>>(new Set());
  const valueRef = useRef(value);

  valueRef.current = value;
  const detectTargetHeader = useCallback((): ParsedHeader | null => {
    const selection = window.getSelection()?.toString() || '';

    if (selection && selection.includes(':')) {
      const colonIndex = selection.indexOf(':');
      const headerName = selection.slice(0, colonIndex).trim();
      const headers = parseHeaders(valueRef.current);
      const matchingHeader = headers.find(
        (h) => h.name.toLowerCase() === headerName.toLowerCase(),
      );

      if (matchingHeader && isAuthHeader(matchingHeader.name)) {
        return matchingHeader;
      }
    }

    return null;
  }, []);
  const handleApplyAllAuth = useCallback(
    (credential: CachedCredentialDto) => {
      const previousCredential = activeCredential
        ? credentials.find(
            (credential) => credential.id === activeCredential.id,
          )
        : undefined;
      const newValue = applyCredentialToRequestText({
        requestText: valueRef.current,
        credential,
        previousCredential,
      });

      onChange(newValue);
      onCredentialApplied?.({
        id: credential.id,
        username: credential.username,
        role: credential.role,
      });
    },
    [onChange, onCredentialApplied, activeCredential, credentials],
  );
  const handleSelectCredentialForHeader = useCallback(
    (credential: CachedCredentialDto, targetHeader: ParsedHeader) => {
      const currentValue = valueRef.current;
      let newValue = currentValue;
      const headerNameLower = targetHeader.name.toLowerCase();

      if (headerNameLower === 'cookie' && credential.cookies.length > 0) {
        const cookieValue = buildCookieHeader(credential.cookies);

        newValue = replaceHeaderValue({
          text: currentValue,
          headerName: targetHeader.name,
          newValue: cookieValue,
        });
      } else if (headerNameLower === 'authorization') {
        const authHeader = credential.headers.find(
          (h) => h.key.toLowerCase() === 'authorization',
        );

        if (authHeader) {
          const preservedValue = preserveAuthScheme({
            originalValue: targetHeader.value,
            newValue: authHeader.value,
          });

          newValue = replaceHeaderValue({
            text: currentValue,
            headerName: targetHeader.name,
            newValue: preservedValue,
          });
        }
      } else {
        const matchingHeader = credential.headers.find(
          (h) => h.key.toLowerCase() === headerNameLower,
        );

        if (matchingHeader) {
          newValue = replaceHeaderValue({
            text: currentValue,
            headerName: targetHeader.name,
            newValue: matchingHeader.value,
          });
        } else {
          const matchingSecret = credential.secrets.find(
            (s) => s.key.toLowerCase() === headerNameLower,
          );

          if (matchingSecret) {
            newValue = replaceHeaderValue({
              text: currentValue,
              headerName: targetHeader.name,
              newValue: matchingSecret.value,
            });
          }
        }
      }

      if (newValue !== currentValue) {
        onChange(newValue);
      }

      onCredentialApplied?.({
        id: credential.id,
        username: credential.username,
        role: credential.role,
      });
    },
    [onChange, onCredentialApplied],
  );
  const handleRefreshCredential = useCallback(
    async (credential: CachedCredentialDto) => {
      if (!applicationId) return;
      setRefreshingIds((prev) => new Set(prev).add(credential.id));
      setRefreshedIds((prev) => {
        const next = new Set(prev);

        next.delete(credential.id);

        return next;
      });

      try {
        const result = await refreshCredentialMutation.mutateAsync({
          applicationId,
          credentialId: credential.id,
          sessionId: `replay-${Date.now()}`,
        });

        onChange(
          applyCredentialToRequestText({
            requestText: valueRef.current,
            credential: result.credential,
            previousCredential: credential,
          }),
        );
        onCredentialApplied?.({
          id: result.credential.id,
          username: result.credential.username,
          role: result.credential.role,
        });
        setRefreshedIds((prev) => new Set(prev).add(credential.id));
      } catch (error) {
        console.error('Failed to refresh credential:', error);
      } finally {
        setRefreshingIds((prev) => {
          const next = new Set(prev);

          next.delete(credential.id);

          return next;
        });
      }
    },
    [applicationId, refreshCredentialMutation, onChange, onCredentialApplied],
  );
  const handleCopyHeader = useCallback((header: ParsedHeader) => {
    navigator.clipboard.writeText(`${header.name}: ${header.value}`);
  }, []);
  // Track target header - updated when context menu opens
  const [targetHeader, setTargetHeader] = useState<ParsedHeader | null>(null);
  // Handle context menu open - detect target header at the right time
  const handleContextMenuOpen = useCallback(() => {
    const detected = detectTargetHeader();

    setTargetHeader(detected);
  }, [detectTargetHeader]);
  // Get credentials with cached auth data for "Run All" feature
  const credentialsForRunAll = useMemo(() => {
    return credentials.filter((credential) =>
      hasReplayRunAllContext({ credential, hasActivePreRequestScript }),
    );
  }, [credentials, hasActivePreRequestScript]);
  const handleRunAllFromContextMenu = useCallback(() => {
    if (!onRunAllCredentials || credentialsForRunAll.length === 0) return;
    const credentialsWithRequests = credentialsForRunAll.map((credential) => ({
      credential,
      requestText: applyCredentialToRequestText({
        requestText: valueRef.current,
        credential,
      }),
    }));

    onRunAllCredentials(credentialsWithRequests);
  }, [onRunAllCredentials, credentialsForRunAll]);
  // Build credential menu content to pass to HttpEditor's context menu
  const credentialMenuContent = useMemo(() => {
    if (!applicationId) return null;

    return (
      <>
        {/* Copy header option if a header is selected */}
        {targetHeader && (
          <>
            <Menu.Item
              value="copy-header"
              onClick={() => handleCopyHeader(targetHeader)}
            >
              <HStack gap="2">
                <Copy01Icon size={14} />
                <LabelDefault>
                  {t('copyHeader', { name: targetHeader.name })}
                </LabelDefault>
              </HStack>
            </Menu.Item>
            <Menu.Separator />
          </>
        )}

        {onRunAllCredentials && credentialsForRunAll.length > 1 && (
          <>
            <Menu.Item value="run-all" onClick={handleRunAllFromContextMenu}>
              <HStack gap="2">
                <PlayCircleIcon size={14} />
                <VStack gap="0" alignItems="flex-start">
                  <LabelDefault>{t('runAllCredentials')}</LabelDefault>
                  <MetaDefault color="fg.muted">
                    {t('runAllCredentialsDescription', {
                      count: credentialsForRunAll.length,
                    })}
                  </MetaDefault>
                </VStack>
              </HStack>
            </Menu.Item>
            <Menu.Separator />
          </>
        )}

        {/* Credentials menu content */}
        <AuthCredentialMenuContent
          credentials={credentials}
          isLoading={isLoadingCredentials}
          targetHeader={targetHeader}
          onSelectCredential={(credential) => {
            if (!targetHeader) {
              handleApplyAllAuth(credential);
            } else {
              handleSelectCredentialForHeader(credential, targetHeader);
            }
          }}
          onRefreshCredential={handleRefreshCredential}
          onApplyAllAuth={handleApplyAllAuth}
          refreshingIds={refreshingIds}
          refreshedIds={refreshedIds}
        />
      </>
    );
  }, [
    applicationId,
    targetHeader,
    handleCopyHeader,
    credentials,
    credentialsForRunAll,
    isLoadingCredentials,
    handleSelectCredentialForHeader,
    handleRefreshCredential,
    handleApplyAllAuth,
    handleRunAllFromContextMenu,
    onRunAllCredentials,
    refreshingIds,
    refreshedIds,
    t,
  ]);
  const handleActiveCredentialChange = useCallback(
    (credential: ActiveCredential) => onCredentialApplied?.(credential),
    [onCredentialApplied],
  );
  const toolbarContent = useMemo(() => {
    if (!applicationId) return undefined;

    return (
      <CredentialToolbarDropdown
        applicationId={applicationId}
        requestText={value}
        onRequestChange={onChange}
        activeCredential={activeCredential}
        onActiveCredentialChange={handleActiveCredentialChange}
        onRunAllCredentials={onRunAllCredentials}
      />
    );
  }, [
    applicationId,
    value,
    onChange,
    activeCredential,
    handleActiveCredentialChange,
    onRunAllCredentials,
  ]);
  const footerErrorMessage = useMemo(() => {
    const error = getHttp1TerminatorError({ requestText: value });

    if (!error) {
      return undefined;
    }

    return 'Missing \\r\\n\\r\\n terminator';
  }, [value]);

  return (
    <Box h={height} w="full" position="relative">
      <HttpEditor
        value={value}
        onChange={onChange}
        height="100%"
        placeholder={placeholder}
        readOnly={readOnly}
        showViewModeToggle
        showGrpcBodyViewToggle={showGrpcBodyViewToggle}
        grpcBodyView={grpcBodyView}
        onGrpcBodyViewChange={onGrpcBodyViewChange}
        grpcBodyViewToggleDisabled={grpcBodyViewToggleDisabled}
        canonicalRequestUrl={canonicalRequestUrl}
        onSendToChat={onSendToChat}
        onSendToReplay={onSendToReplay}
        onContextMenuOpen={handleContextMenuOpen}
        toolbarContent={toolbarContent}
        contextMenuContent={credentialMenuContent}
        hideBuiltInActions={HIDDEN_BUILT_IN_ACTIONS}
        footerErrorMessage={footerErrorMessage}
      />
    </Box>
  );
});
